const crcTable = Array.from({ length: 256 }, (_, value) => {
  let crc = value
  for (let bit = 0; bit < 8; bit += 1) crc = (crc & 1) ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1
  return crc >>> 0
})

function crc32(bytes) {
  let crc = 0xffffffff
  bytes.forEach(byte => { crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8) })
  return (crc ^ 0xffffffff) >>> 0
}

function uint16(value) {
  return [value & 0xff, (value >>> 8) & 0xff]
}

function uint32(value) {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff]
}

function buildZip(files) {
  const encoder = new TextEncoder()
  const localParts = []
  const centralParts = []
  let offset = 0

  files.forEach(file => {
    const name = encoder.encode(file.name)
    const data = encoder.encode(file.content || '')
    const checksum = crc32(data)
    const localHeader = new Uint8Array([
      ...uint32(0x04034b50), ...uint16(20), ...uint16(0x0800), ...uint16(0), ...uint16(0), ...uint16(0),
      ...uint32(checksum), ...uint32(data.length), ...uint32(data.length), ...uint16(name.length), ...uint16(0),
    ])
    localParts.push(localHeader, name, data)

    const centralHeader = new Uint8Array([
      ...uint32(0x02014b50), ...uint16(20), ...uint16(20), ...uint16(0x0800), ...uint16(0), ...uint16(0), ...uint16(0),
      ...uint32(checksum), ...uint32(data.length), ...uint32(data.length), ...uint16(name.length), ...uint16(0), ...uint16(0),
      ...uint16(0), ...uint16(0), ...uint32(file.name.endsWith('/') ? 0x10 : 0), ...uint32(offset),
    ])
    centralParts.push(centralHeader, name)
    offset += localHeader.length + name.length + data.length
  })

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0)
  const endRecord = new Uint8Array([
    ...uint32(0x06054b50), ...uint16(0), ...uint16(0), ...uint16(files.length), ...uint16(files.length),
    ...uint32(centralSize), ...uint32(offset), ...uint16(0),
  ])
  return new Blob([...localParts, ...centralParts, endRecord], { type: 'application/zip' })
}

const copyTemplate = `标题：
在这里填写作品标题

正文：
在这里填写作品正文，可以写多行

标签：（每行填写一个标签，不要加 #）
咖啡
通勤
办公室

图片顺序：（每行填写一个图片文件名）
01.jpg
02.jpg
03.jpg
`

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function createImageWorkPackageTemplateBlob() {
  const instructions = `批量图文作品素材包使用说明

1. 每条作品使用一个独立文件夹，可复制示例文件夹继续添加作品。
2. 每个作品文件夹内都必须包含“作品文案.txt”和 images 文件夹。
3. 将该作品的图片放入对应 images 文件夹，建议按 01.jpg、02.jpg 命名。
4. 打开对应的“作品文案.txt”，填写标题、正文、标签和图片顺序。
5. “图片顺序”中的文件名必须和当前作品 images 文件夹内的图片一致。
6. 完成后将所有作品文件夹一起压缩为一个 ZIP 上传。
`
  return buildZip([
    { name: '001-示例作品A/作品文案.txt', content: copyTemplate },
    { name: '001-示例作品A/images/', content: '' },
    { name: '002-示例作品B/作品文案.txt', content: copyTemplate },
    { name: '002-示例作品B/images/', content: '' },
    { name: '批量上传使用说明.txt', content: instructions },
  ])
}

export function downloadImageWorkPackageTemplate() {
  downloadBlob(createImageWorkPackageTemplateBlob(), '图文批量作品模板.zip')
}

export function createVideoWorkPackageTemplateBlob() {
  const videoCopyTemplate = copyTemplate.replace('图片顺序：（每行填写一个图片文件名）\n01.jpg\n02.jpg\n03.jpg', '视频文件名：\n作品视频.mp4')
  const instructions = `批量视频作品素材包使用说明

1. 每条作品使用一个独立文件夹，每个文件夹内都需要“作品文案.txt”和 video 文件夹。
2. 每条视频作品只能在对应 video 文件夹中放入一个视频。
3. 填写每条作品的标题、正文、标签和视频文件名。
4. 可复制示例文件夹继续添加作品。
5. 完成后将所有作品文件夹一起压缩为一个 ZIP 上传。
`
  return buildZip([
    { name: '001-示例作品A/作品文案.txt', content: videoCopyTemplate },
    { name: '001-示例作品A/video/', content: '' },
    { name: '002-示例作品B/作品文案.txt', content: videoCopyTemplate },
    { name: '002-示例作品B/video/', content: '' },
    { name: '批量上传使用说明.txt', content: instructions },
  ])
}

export function downloadVideoWorkPackageTemplate() {
  downloadBlob(createVideoWorkPackageTemplateBlob(), '视频批量作品模板.zip')
}

export function createLiveWorkPackageTemplateBlob() {
  const liveCopyTemplate = copyTemplate.replace('图片顺序：（每行填写一个图片文件名）\n01.jpg\n02.jpg\n03.jpg', '素材顺序：（每行填写一个图片或视频文件名）\n01.jpg\n02.mp4\n03.jpg')
  const instructions = `批量 Live 图作品素材包使用说明

1. 每条作品使用一个独立文件夹，每个文件夹内都需要“作品文案.txt”和 media 文件夹。
2. 将该作品的图片和视频放入对应 media 文件夹。
3. 填写每条作品的标题、正文、标签和素材顺序。
4. 可复制示例文件夹继续添加作品。
5. 完成后将所有作品文件夹一起压缩为一个 ZIP 上传。
`
  return buildZip([
    { name: '001-示例作品A/作品文案.txt', content: liveCopyTemplate },
    { name: '001-示例作品A/media/', content: '' },
    { name: '002-示例作品B/作品文案.txt', content: liveCopyTemplate },
    { name: '002-示例作品B/media/', content: '' },
    { name: '批量上传使用说明.txt', content: instructions },
  ])
}

export function downloadLiveWorkPackageTemplate() {
  downloadBlob(createLiveWorkPackageTemplateBlob(), 'Live图批量作品模板.zip')
}
